import {
  PRODUCT_IMPRESSION_DWELL_MS,
  PRODUCT_IMPRESSION_THRESHOLD,
  trackImpression,
} from './analytics';

/** @type {IntersectionObserver|null} */
let sharedObserver = null;

/** @type {WeakMap<Element, () => ({
 *   listingProductId: string|number,
 *   query?: string|null,
 *   shopCategorySlug?: string|null,
 *   source?: string,
 * }|null)>}
 */
const contextGetters = new WeakMap();

/** @type {WeakMap<Element, number>} */
const dwellTimers = new WeakMap();

function clearDwellTimer(el) {
  const timerId = dwellTimers.get(el);
  if (timerId != null) {
    window.clearTimeout(timerId);
    dwellTimers.delete(el);
  }
}

function ensureObserver() {
  if (sharedObserver != null) {
    return sharedObserver;
  }

  sharedObserver = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        const el = entry.target;
        if (!entry.isIntersecting || entry.intersectionRatio < PRODUCT_IMPRESSION_THRESHOLD) {
          clearDwellTimer(el);
          continue;
        }

        if (typeof document !== 'undefined' && document.visibilityState === 'hidden') {
          clearDwellTimer(el);
          continue;
        }

        if (dwellTimers.has(el)) {
          continue;
        }

        const timerId = window.setTimeout(() => {
          dwellTimers.delete(el);
          if (typeof document !== 'undefined' && document.visibilityState === 'hidden') {
            return;
          }
          const getContext = contextGetters.get(el);
          if (!getContext) {
            return;
          }
          const context = getContext();
          if (!context || context.listingProductId == null) {
            return;
          }
          trackImpression(context);
          sharedObserver?.unobserve(el);
          contextGetters.delete(el);
        }, PRODUCT_IMPRESSION_DWELL_MS);

        dwellTimers.set(el, timerId);
      }
    },
    { threshold: PRODUCT_IMPRESSION_THRESHOLD },
  );

  return sharedObserver;
}

/**
 * Observe a product card root for a once-per-page-view viewport impression.
 *
 * @param {Element} el
 * @param {() => ({
 *   listingProductId: string|number,
 *   query?: string|null,
 *   shopCategorySlug?: string|null,
 *   source?: string,
 * }|null)} getContext
 */
export function observeProductImpression(el, getContext) {
  if (typeof IntersectionObserver === 'undefined') {
    return;
  }
  contextGetters.set(el, getContext);
  ensureObserver().observe(el);
}

/**
 * @param {Element} el
 */
export function unobserveProductImpression(el) {
  clearDwellTimer(el);
  contextGetters.delete(el);
  sharedObserver?.unobserve(el);
}
